import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, tap, switchMap, map, first } from 'rxjs/operators';

import { UserSettingsApiService } from '../../core/services/api/user-settings-api.service';
import { DashboardStateService } from '../../dashboard/dashboard-state-service/dashboard-state.service';
import { DashboardService } from '../../dashboard/dashboard-service/dashboard.service';
import { RequestTrackerService } from '../../core-services/request-tracker-service/request-tracker.service';
import { DashboardPermissionsService } from '../../dashboard/dashboard-permissions-service/dashboard-permissions.service';
import { DashboardApiService } from '../../core/services/api/dashboard-api.service';
import { MerchantApiService } from '../../core/services/api/merchant-api.service';
import { AccountPermission, EntityGroupName, MerchantWebsite, UsersAvailableAccount } from '../../core/dtos/api';
import { RoutingService } from '../../core/services/routing.service';
import { DashboardConfig, SnapshotSettings } from '../../core/widgets';
import { MerchantGroupApiService } from '../../core/services/api/merchant-group-api.service';
import { Router } from '@angular/router';
import { Dashboard } from '../snapshot/snapshot';

@Component({
    selector: 'app-agency-snapshot',
    templateUrl: './agency-snapshot.component.html',
    styleUrls: ['./agency-snapshot.component.scss'],
    standalone: false
})
export class AgencySnapshotComponent implements OnDestroy {
  public dashboard: Dashboard = this.router.url.indexOf('agency') !== -1 ? 'agency' : 'key_accounts';
  public entityConfig: DashboardConfig;
  public usersSelectedMerchants: UsersAvailableAccount[];
  public snapshotRequestTracker$ = this.requestTracker.affiliateSnapshotRequestTracker$
    .pipe(
      // Added to remedy expression changed angular warning in dev.
      tap(() => this.changeDetectorRef.detectChanges())
    );

  public settings: SnapshotSettings = {
    entityId: '',
    entityType: '',
    currencyFilter: 'USD',
    networkFilter: 'US'
  };
  private previousAccountEntity: EntityGroupName;
  public accountPermissions: AccountPermission[];
  public userMerchants: UsersAvailableAccount[];
  isUserRoot$ = this.dashboardApiService.isRootUser$;

  private checkForRootUserRedirect$Sub = combineLatest([this.dashboardPermissionsService.accountWithPermissions$, this.isUserRoot$])
    .subscribe(([[currentAccount, ], isUserRoot]) => {
    if (isUserRoot) {
      if (this.previousAccountEntity && (this.previousAccountEntity !== currentAccount.entity_group_name)) {
        this.routingService.directToDashboard();
        return;
      } else {
        if (currentAccount.entity_group_name !== 'merchant_groups' && currentAccount.entity_group_name !== 'root') {
          this.routingService.directToDashboard();
          return;
        }
      }
    }
  });

  private gatherSnapshotInputsData$Sub = this.dashboardPermissionsService.accountWithPermissions$.pipe(
    switchMap(accountWithPermissions =>
      combineLatest([
        this.userSettingsApi.getUserDashboardConfig(this.dashboard, this.dashboard).pipe(
          catchError(x => of(null))),
        <Observable<any>> this.dashboardApiService.availableAccounts$.pipe(
          first(),
          switchMap(userAccounts => {
            let entityGroupEntityId: string[];
            let returnedMerchantAccounts: UsersAvailableAccount[];
            if (userAccounts.some(singleAccount => singleAccount.entity_group_name === 'root')) {
              if (accountWithPermissions[0].entity_group_name === 'merchant_groups') {
                entityGroupEntityId = [accountWithPermissions[0].entity_id];
              } else {
                return this.merchantApi.getAllMerchants().pipe(
                  map(merchants => merchants.map(singleMerchant => ({
                        entity_group_name: <EntityGroupName> singleMerchant.merchant_name,
                        entity_name: singleMerchant.merchant_name,
                        classic_entity_id: singleMerchant.classic_merchant_id,
                        entity_id: singleMerchant.merchant_id,
                        entity_network: singleMerchant.merchant_network,
                        entity_websites: singleMerchant.merchant_websites
                      })))
                );
              }
            }
            returnedMerchantAccounts = userAccounts.filter(userAccount => userAccount.entity_group_name === 'merchants');
            if (entityGroupEntityId?.length > 0) {
              return this.merchantGroupApi.getMerchantGroupInfo(entityGroupEntityId).pipe(
                map(merchantGroupInfo => {
                  let additionalMerchants: UsersAvailableAccount[] = [];
                  merchantGroupInfo.forEach((singleGroup) => {
                    let merchants: UsersAvailableAccount[] = singleGroup.merchants.filter(singleAccount =>
                      // If the merchant has already been added to returnedMerchantAccounts don't add it again.
                       singleAccount.merchant_status === 'active'
                        && !returnedMerchantAccounts.find((singleReturnedMerchantAccount) => singleReturnedMerchantAccount.entity_id === singleAccount.merchant_id)
                    )
                      .map(singleMerchant => ({
                          entity_group_name: <EntityGroupName> singleMerchant.merchant_name,
                          entity_name: singleMerchant.merchant_name,
                          classic_entity_id: singleMerchant.classic_merchant_id,
                          entity_id: singleMerchant.merchant_id,
                          entity_network: singleMerchant.merchant_network,
                          entity_websites: singleMerchant.merchant_websites,
                          entity_status: singleMerchant.merchant_status,
                          updated_at: singleMerchant.updated_at
                        }));
                    additionalMerchants = [...additionalMerchants, ...merchants];
                  });
                  return [...returnedMerchantAccounts, ...additionalMerchants];
                })
              );
            } else {
              return of(returnedMerchantAccounts);
            }
          }),
          catchError(x => of(null))),
        this.userSettingsApi.getDefaultCurrency(accountWithPermissions[0]),
        of(accountWithPermissions),
        this.isUserRoot$.pipe(
          first(),
          catchError(x => of(null)))
      ])
    )
  ).subscribe(([userSettings, merchantAccounts, defaultCurrency, [currentAccount, accountPermissions], isUserRoot]) => {
    let usersSelectedMerchants = [];

    // For non-root users check to see if we need to redirect. Root check is done above in checkForRootUserRedirect$Sub;
    if (!isUserRoot) {
      if ((currentAccount.entity_group_name !== 'merchant_groups' && currentAccount.entity_group_name !== 'merchants')) {
        this.routingService.directToDashboard();
        return;
      }
    }

    if (!isUserRoot) {
      usersSelectedMerchants = userSettings.selectedMerchants
        ? merchantAccounts.filter(singleMerchant => userSettings.selectedMerchants.indexOf(singleMerchant.entity_id) !== -1) : merchantAccounts;
    } else {
      if (this.dashboard === 'agency') {
        usersSelectedMerchants = merchantAccounts;
      } else {
        // Root key_account users get back empty array if there is no saved accounts, as we allow searching all available
        // accounts in the snapshot component.
        usersSelectedMerchants = userSettings.selectedMerchants
          ? merchantAccounts.filter(singleMerchant => userSettings.selectedMerchants.indexOf(singleMerchant.entity_id) !== -1) : [];
      }
    }

    this.usersSelectedMerchants = usersSelectedMerchants;
    this.previousAccountEntity = currentAccount.entity_group_name;
    this.settings = {
      entityId: currentAccount.entity_id,
      entityType: currentAccount.entity_group_name,
      currencyFilter: defaultCurrency,
      networkFilter: currentAccount.entity_network,
    };

    this.entityConfig = userSettings;
    this.accountPermissions = accountPermissions;
    this.userMerchants = merchantAccounts;
  });

  constructor(
    private userSettingsApi: UserSettingsApiService,
    private dashboardService: DashboardService,
    private merchantApi: MerchantApiService,
    public dashboardStateService: DashboardStateService,
    public requestTracker: RequestTrackerService,
    private routingService: RoutingService,
    private dashboardPermissionsService: DashboardPermissionsService,
    private dashboardApiService: DashboardApiService,
    private changeDetectorRef: ChangeDetectorRef,
    private merchantGroupApi: MerchantGroupApiService,
    private router: Router
  ) {}

  ngOnDestroy(): void {
    this.gatherSnapshotInputsData$Sub.unsubscribe();
    this.checkForRootUserRedirect$Sub.unsubscribe();
  }
}
